<template>
  <nav
    v-if="pages > 1"
    class="text-center overflow-hidden w-full"
  >
    <div class="text-secondary">
      Sivu {{ currentPage }} / {{ pages }}
    </div>
    <div class="flex justify-center pt-4 pb-10">
      <BaseButton
        v-show="currentPage === pages"
        aria-label="Ensimmäiselle sivulle"
        title="Ensimmäiselle sivulle"
        :href="`?${urlParam}=1`"
        data-ab="nav-first-page"
        @click.prevent="toPage(1)"
      >
        <ChevronLeftIcon class="size-6 mr-2" /> Ensimmäiselle sivulle
      </BaseButton>
      <BaseButton
        v-show="currentPage !== pages"
        aria-label="Seuraava sivu"
        title="Seuraava sivu"
        :href="`?${urlParam}=${+(currentPage+1)}`"
        rel="next"
        data-ab="nav-next-page"
        @click.prevent="nextPage"
      >
        Seuraava sivu <ChevronRightIcon class="size-6 ml-2" />
      </BaseButton>
    </div>
    <ul class="list-reset grid grid-flow-col gap-1 sm:gap-1 justify-center text-xs">
      <li class="whitespace-nowrap items-center flex">
        <BaseButton
          aria-label="Edellinen sivu"
          title="Edellinen sivu"
          look="ghost"
          class="size-[3em] p-0"
          :class="{ 'opacity-0 pointer-events-none': currentPage === 1 }"
          :disabled="currentPage === 1"
          :href="`?${urlParam}=${+(currentPage-1)}`"
          rel="prev"
          data-ab="nav-prev-page"
          @click.prevent="prevPage"
        >
          <span>
            <ChevronLeftIcon class="size-6" />
          </span>
        </BaseButton>
      </li>
      <li
        v-for="(page, i) in items"
        :key="i+'_'+page"
        class="whitespace-nowrap items-center md:flex"
        :data-current="currentPage === page"
      >
        <span v-if="isEllipsis(page)" class="text-xs sm:text-lg size-8 flex items-center justify-center bg-transparent">
          {{ page }}
        </span>
        <BaseButton
          v-else
          look="ghost"
          :class="{ 'border bg-surface-raised text-red-800 dark:text-red-950 pointer-events-none': currentPage == page }"
          class="size-[3em] p-0"
          :href="`?${urlParam}=${+page}`"
          :data-ab="`nav-page-${page}`"
          @click.prevent="toPage(page as number)"
        >
          {{ page }}
        </BaseButton>
      </li>
      <li class="whitespace-nowrap items-center flex">
        <BaseButton
          look="ghost"
          aria-label="Seuraava sivu"
          title="Seuraava sivu"
          class="size-[3em] p-0"
          :class="{ 'opacity-0 pointer-events-none': currentPage === pages }"
          :href="`?${urlParam}=${+(currentPage+1)}`"
          rel="next"
          data-ab="nav-next-page"
          @click.prevent="nextPage"
        >
          <span>
            <ChevronRightIcon class="size-6" />
          </span>
        </BaseButton>
      </li>
    </ul>
  </nav>
</template>

<script setup lang="ts">
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/24/outline'

const emit = defineEmits(['pageChange'])
const props = defineProps({
  perPage: {
    type: Number,
    required: false,
    default: 9
  },
  itemAmount: {
    type: Number,
    required: false,
    default: 9
  },
  currentPage: {
    type: Number,
    required: false,
    default: 1
  },
  urlParam: {
    type: String,
    required: false,
    default: 'sivu'
  }
})

type Ellipsis = string
const ellipsis = ref<Ellipsis>('...')
const maxTiles = ref(5)

const pages = computed(() => Math.ceil(props.itemAmount / props.perPage))
const tileAmount = computed(() => pages.value < maxTiles.value ? pages.value : maxTiles.value)
const isFullSize = computed(() => pages.value > maxTiles.value)
const items = computed(() => {
  const tilesOnOneSide = Math.floor((tileAmount.value - 1) / 2)
  const diff = (props.currentPage - tilesOnOneSide <= 0) ? props.currentPage - 1 : (props.currentPage + tilesOnOneSide >= pages.value) ? tileAmount.value - 1 - (pages.value - props.currentPage) : tilesOnOneSide
  const arr: (number | Ellipsis)[] = Array.from({ length: pages.value }, (_, i) => props.currentPage - diff + i).slice(0, tileAmount.value)
  if (isFullSize.value) {
    arr[0] = 1
    arr[1] = (props.currentPage > tileAmount.value - tilesOnOneSide) ? ellipsis.value : arr[1]
    arr[tileAmount.value - 2] = (props.currentPage + tilesOnOneSide < pages.value) ? ellipsis.value : arr[tileAmount.value - 2]
    arr[tileAmount.value - 1] = pages.value
  }
  return arr
})

const isEllipsis = (page: number | Ellipsis) => page === ellipsis.value
const toPage = (page: number) => emit('pageChange', page)
const prevPage = () => {
  if (props.currentPage > 1) toPage(props.currentPage - 1)
}
const nextPage = () => {
  if (props.currentPage < pages.value) toPage(props.currentPage + 1)
}

onMounted(() => {
  if (window) {
    if (window.innerWidth > 420) {
      maxTiles.value = 7
    }
    if (window.innerWidth > 520) {
      maxTiles.value = 9
    }
  }
})
</script>
